import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Snackbar,
    CircularProgress,
    Divider,
} from "@mui/material"
import DescriptionIcon from "@mui/icons-material/Description"
import SaveIcon from "@mui/icons-material/Save"
import PreviewIcon from "@mui/icons-material/Visibility"
import LogoUpload from "./LogoUpload"
import DocumentPreviewDialog from "./DocumentPreviewDialog"
import {
    getOutletDocumentSettings,
    upsertOutletDocumentSettings,
    previewOutletDocument,
} from "../../services/outletSettingsService"
import { useTranslation } from "react-i18next"

interface DocumentCustomizationSectionProps {
    companyId: string
    outletId: string
}

const FOOTER_MAX = 1000

const emptyForm = {
    trade_name: "",
    address_street: "",
    address_postal_code: "",
    address_city: "",
    address_country: "FR",
    phone: "",
    email: "",
    siret: "",
    vat_intracom: "",
    rcs: "",
    naf_ape_code: "",
    footer_text: "",
}

const DocumentCustomizationSection: React.FC<
    DocumentCustomizationSectionProps
> = ({ companyId, outletId }) => {
    const { t } = useTranslation()
    const [form, setForm] = useState({ ...emptyForm })
    const [logoUrl, setLogoUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewHtml, setPreviewHtml] = useState("")
    const [previewLoading, setPreviewLoading] = useState(false)
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({ open: false, message: "", severity: "success" })

    const loadSettings = useCallback(async () => {
        if (!companyId || !outletId) return
        setLoading(true)
        try {
            const data = await getOutletDocumentSettings(companyId, outletId)
            if (data && Object.keys(data).length > 0) {
                setForm({
                    trade_name: data.trade_name || "",
                    address_street: data.address_street || "",
                    address_postal_code: data.address_postal_code || "",
                    address_city: data.address_city || "",
                    address_country: data.address_country || "FR",
                    phone: data.phone || "",
                    email: data.email || "",
                    siret: data.siret || "",
                    vat_intracom: data.vat_intracom || "",
                    rcs: data.rcs || "",
                    naf_ape_code: data.naf_ape_code || "",
                    footer_text: data.footer_text || "",
                })
                setLogoUrl(data.logo_url || null)
            } else {
                setForm({ ...emptyForm })
                setLogoUrl(null)
            }
            setDirty(false)
            setErrors({})
        } catch (e: any) {
            setSnackbar({
                open: true,
                message: String(
                    t("settings.documentCustomization.toast.loadError")
                ),
                severity: "error",
            })
        } finally {
            setLoading(false)
        }
    }, [companyId, outletId, t])

    useEffect(() => {
        loadSettings()
    }, [loadSettings])

    const handleChange = (field: string, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setDirty(true)
        // Clear field error on change
        if (errors[field]) {
            setErrors((prev) => {
                const next = { ...prev }
                delete next[field]
                return next
            })
        }
    }

    const validate = (): boolean => {
        const errs: Record<string, string> = {}
        if (form.siret && !/^\d{11}$|^\d{14}$/.test(form.siret)) {
            errs.siret = String(t("settings.documentCustomization.error.siret"))
        }
        if (form.vat_intracom && !/^FR\w{11}$/.test(form.vat_intracom)) {
            errs.vat_intracom = String(
                t("settings.documentCustomization.error.vatFormat")
            )
        }
        if (form.footer_text && form.footer_text.length > FOOTER_MAX) {
            errs.footer_text = String(
                t("settings.documentCustomization.error.footerMax", {
                    count: FOOTER_MAX,
                })
            )
        }
        setErrors(errs)
        return Object.keys(errs).length === 0
    }

    const handleSave = async () => {
        if (!validate()) return
        setSaving(true)
        try {
            await upsertOutletDocumentSettings(companyId, outletId, form)
            setDirty(false)
            setSnackbar({
                open: true,
                message: String(
                    t("settings.documentCustomization.toast.saved")
                ),
                severity: "success",
            })
        } catch (e: any) {
            setSnackbar({
                open: true,
                message:
                    e.message ||
                    String(t("settings.documentCustomization.toast.saveError")),
                severity: "error",
            })
        } finally {
            setSaving(false)
        }
    }

    const handlePreview = async () => {
        setPreviewLoading(true)
        try {
            const html = await previewOutletDocument(companyId, outletId, form)
            setPreviewHtml(html)
            setPreviewOpen(true)
        } catch (e: any) {
            setSnackbar({
                open: true,
                message: String(
                    t("settings.documentCustomization.toast.previewError")
                ),
                severity: "error",
            })
        } finally {
            setPreviewLoading(false)
        }
    }

    const handleLogoChange = (url: string | null, _mime: string | null) => {
        setLogoUrl(url)
    }

    if (loading) {
        return (
            <>
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                    }}
                >
                    <DescriptionIcon sx={{ color: "#6366f1" }} />
                    {t("settings.documentCustomization.title")}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            </>
        )
    }

    return (
        <>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                    <DescriptionIcon sx={{ color: "#6366f1" }} />
                    {t("settings.documentCustomization.title")}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                            previewLoading ? (
                                <CircularProgress size={14} />
                            ) : (
                                <PreviewIcon />
                            )
                        }
                        disabled={previewLoading}
                        onClick={handlePreview}
                        sx={{ textTransform: "none" }}
                    >
                        {t("settings.documentCustomization.preview")}
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={
                            saving ? (
                                <CircularProgress size={16} />
                            ) : (
                                <SaveIcon />
                            )
                        }
                        disabled={!dirty || saving}
                        onClick={handleSave}
                        sx={{
                            textTransform: "none",
                            bgcolor: "#6366f1",
                            "&:hover": { bgcolor: "#4f46e5" },
                        }}
                    >
                        {t("common.save")}
                    </Button>
                </Box>
            </Box>

            {/* Logo Upload */}
            <LogoUpload
                companyId={companyId}
                outletId={outletId}
                logoUrl={logoUrl}
                onLogoChange={handleLogoChange}
            />

            <Divider sx={{ my: 2 }} />

            {/* Business Information */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("settings.documentCustomization.sections.business")}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 3,
                }}
            >
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.tradeName")
                    )}
                    size="small"
                    value={form.trade_name}
                    onChange={(e) => handleChange("trade_name", e.target.value)}
                    placeholder={String(
                        t(
                            "settings.documentCustomization.fields.tradeNamePlaceholder"
                        )
                    )}
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.phone")
                    )}
                    size="small"
                    value={form.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.email")
                    )}
                    size="small"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.siret")
                    )}
                    size="small"
                    value={form.siret}
                    onChange={(e) => handleChange("siret", e.target.value)}
                    error={!!errors.siret}
                    helperText={
                        errors.siret ||
                        t("settings.documentCustomization.fields.siretHint")
                    }
                    inputProps={{ maxLength: 14 }}
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.vat")
                    )}
                    size="small"
                    value={form.vat_intracom}
                    onChange={(e) =>
                        handleChange("vat_intracom", e.target.value)
                    }
                    error={!!errors.vat_intracom}
                    helperText={
                        errors.vat_intracom ||
                        t("settings.documentCustomization.fields.vatHint")
                    }
                    inputProps={{ maxLength: 13 }}
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.rcs")
                    )}
                    size="small"
                    value={form.rcs}
                    onChange={(e) => handleChange("rcs", e.target.value)}
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.nafApe")
                    )}
                    size="small"
                    value={form.naf_ape_code}
                    onChange={(e) =>
                        handleChange("naf_ape_code", e.target.value)
                    }
                />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Address */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("settings.documentCustomization.sections.address")}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    mb: 3,
                }}
            >
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.street")
                    )}
                    size="small"
                    value={form.address_street}
                    onChange={(e) =>
                        handleChange("address_street", e.target.value)
                    }
                    sx={{ gridColumn: { sm: "1 / -1" } }}
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.postalCode")
                    )}
                    size="small"
                    value={form.address_postal_code}
                    onChange={(e) =>
                        handleChange("address_postal_code", e.target.value)
                    }
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.city")
                    )}
                    size="small"
                    value={form.address_city}
                    onChange={(e) =>
                        handleChange("address_city", e.target.value)
                    }
                />
                <TextField
                    label={String(
                        t("settings.documentCustomization.fields.country")
                    )}
                    size="small"
                    value={form.address_country}
                    onChange={(e) =>
                        handleChange("address_country", e.target.value)
                    }
                />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Footer Text */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                {t("settings.documentCustomization.sections.footer")}
            </Typography>

            <TextField
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                size="small"
                value={form.footer_text}
                onChange={(e) => handleChange("footer_text", e.target.value)}
                error={!!errors.footer_text}
                helperText={
                    errors.footer_text ||
                    t("settings.documentCustomization.footerCount", {
                        count: form.footer_text.length,
                        max: FOOTER_MAX,
                    })
                }
                placeholder={String(
                    t("settings.documentCustomization.footerPlaceholder")
                )}
                inputProps={{ maxLength: FOOTER_MAX }}
                sx={{ mb: 2 }}
            />

            {/* Preview Dialog */}
            <DocumentPreviewDialog
                open={previewOpen}
                html={previewHtml}
                onClose={() => setPreviewOpen(false)}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    )
}

export default DocumentCustomizationSection
